"use client";

import React from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { AnimatePresence } from "framer-motion";
import { useTranslations } from "next-intl";
import Image from 'next/image';
import { EventsEndpoints } from '@/graph/event/endpoints';
import { RawDraftContentState } from "draft-js";
import Link from "next/link";
import { useParams } from "next/navigation";
import TextLinker from "@apg.gg/core/lib/TextLinker";
import Tag from "@apg.gg/core/lib/Tag";
import EventTypeEnum from "@/domain/events/eventType.enum";
import EventCategoryEnum from "@/domain/events/eventCategory.enum";
import { format } from 'date-fns';
import { es, enGB, ptBR } from 'date-fns/locale';
import InfiniteScroll from "@/components/InfiniteScroll";
import CardProfileFollow from "@/components/ui/CardProfileFollow";
import logError from "@/lib/utils/logError";
import { ErrorBoundary } from "react-error-boundary";
import ErrorCardProfileFallback from "../ErrorCardProfile";
import Button from "@apg.gg/core/lib/Button";
import { useJoinEvent } from "@/hooks/useEventHooks";
import useAuthStore from "@/store/auth.store";
import PulseRingIcon from "@apg.gg/icons/lib/PulseRingIcon";
import { toast } from "react-toastify";
import { useRouter } from "next-nprogress-bar";
import EventTagEnum from "@/domain/events/eventTag.enum";
import CrownIcon from "@apg.gg/icons/lib/CrownIcon";

interface Props {
  slug: string;
  userId: number;
}

const EventDetailComponent = ({
  slug,
  userId
}: Props) => {
  const t = useTranslations('events');
  const router = useRouter();
  const { authToken } = useAuthStore();
  const { locale } = useParams();
  const joinEventMutation = useJoinEvent({ eventSlug: slug, token: authToken });

  const { data: event, refetch: refetchEvent } = useQuery({
    queryKey: ['apg-event-detail', slug],
    queryFn: () => EventsEndpoints.getEventBySlug({ slug, userId })
  });

  const {
    status,
    data,
    error,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ['apg-event-participants', slug],
    queryFn: async ({ pageParam }) => await EventsEndpoints.getEventParticipants({
      slug,
      page: pageParam,
      userId: userId
    }),
    initialPageParam: 1,
    getPreviousPageParam: (firstPage) => firstPage.previousPage ?? undefined,
    getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined
  })

  const hasRichDescription = (shortRichDescription: RawDraftContentState) => {
    if (!shortRichDescription || shortRichDescription === undefined) return false;

    if ('blocks' in shortRichDescription && 'entityMap' in shortRichDescription) {
      // Verificar si blocks es un array con al menos un elemento
      if (Array.isArray(shortRichDescription.blocks) && shortRichDescription.blocks.length > 0) {
        return true;
      }
    }

    return false;
  };

  const getLocale = (locale: string) => {
    switch (locale) {
      case 'es':
        return es;
      case 'en':
        return enGB;
      case 'pt':
        return ptBR;
      default:
        return es;
    }
  }

  const getDate = (date: string) => {
    const dateSplited = date.split("-");
    const year = parseInt(dateSplited[0]);
    const month = parseInt(dateSplited[1]) - 1;
    const day = parseInt(dateSplited[2]);

    return format(new Date(year, month, day), "dd MMM yyyy", { locale: getLocale(locale as string) })
  }

  const handleJoinEvent = async () => {
    if (authToken) {
      const success = await joinEventMutation.mutateAsync(event?.id as number);
  
      if (success) {
        toast.success(t('toast-success-join-event'));
        refetchEvent();
      } else {
        toast.error(t('toast-error-join-event'));
      }
    } else {
      router.push(`/${locale}/auth/login`);
    }
  }

  return (
    <>
      <section id="event-detail" className="row my-8 space-y-8">
        {event ? (
          <div className="container flex flex-col gap-8">
            <div className="flex flex-col bg-black-900 px-4 sm:px-8 py-4 sm:py-8 gap-6 rounded-2xl items-center">
              <div className="flex flex-col sm:flex-row items-stretch gap-4 w-full">
                <div className="flex flex-col gap-4">
                  <div className="relative w-full sm:w-[225px] sm:h-[300px] md:w-[300px] md:h-[400px] shadow-2xl rounded-2xl overflow-hidden">
                    <div className="w-full h-0 pb-[133.33%] sm:pb-0 sm:w-[225px] sm:h-[300px] md:w-[300px] md:h-[400px] relative">
                      <div className="absolute inset-0 w-full h-full">
                        <Image
                          src={event.image || ""}
                          alt={event.title}
                          width={1200}
                          height={1600}
                          className="w-full h-full sm:w-[225px] sm:h-[300px] md:w-[300px] md:h-[400px] object-cover object-center"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-1 flex-col justify-start gap-2">
                  <h1 className="text-headline-sm sm:text-headline-md lg:text-display-md">{event.title}</h1>
                  <div className="max-w-5xl">
                    {hasRichDescription(event.description) ? (
                      <TextLinker content={event.description} linkComponent={Link} locale={locale as string} />
                    ) : (
                      <TextLinker text={event.descriptionText} linkComponent={Link} locale={locale as string} />
                    )}
                  </div>

                  <div className="flex flex-col gap-4 mt-auto">
                    <div className="flex text-label-md text-black-500 gap-1">
                      <span className="text-white-400 text-xs">{event.game?.name || 'General'}</span>
                      <span className="text-white-400"> - </span>
                      <span className="text-white-400 text-xs">{getDate(String(event.date))}</span>
                    </div>

                    <div className="flex justify-start items-center gap-1 mt-auto">
                      {event.eventTag.name ? (
                        <Tag
                          type={EventTagEnum[event.eventTag.name as keyof typeof EventTagEnum] as any}
                          className="max-w-none h-6"
                          icon={event.eventTag.name === 'Oficial' ? <CrownIcon /> : undefined}
                        >
                          {event.eventTag.name}
                        </Tag>
                      ) : null}
                      {event.eventType.name ? (
                        <Tag
                          type={EventTypeEnum[event.eventType.name as keyof typeof EventTypeEnum] as any}
                          className="max-w-none h-6"
                        >
                          {event.eventType.name}
                        </Tag>
                      ) : null}
                      {event.eventCategory.name ? (
                        <Tag
                          type={EventCategoryEnum[event.eventCategory.name as keyof typeof EventCategoryEnum] as any}
                          className="max-w-none h-6"
                        >
                          {event.eventCategory.name}
                        </Tag>
                      ) : null}
                      {event.profileType.name ? (
                        <Tag
                          type={event.profileType.color}
                          icon={event.profileType.icon}
                          className="max-w-none h-6"
                        >
                          {event.profileType.name}
                        </Tag>
                      ) : null}
                    </div>

                    <div className="flex w-full justify-between items-center">
                      <div className="flex gap-2 items-center">
                        <Image className="rounded-full w-10 h-10 object-cover" src={event.owner.profileImage} alt={event.owner.username} width={40} height={40} />
                        <div className="flex flex-col justify-center">
                          <h4 className="text-label-lg text-white">
                            @{event.owner.username}
                          </h4>
                          <Link href={`/${locale}/${event.owner.username}`} className="text-label-md text-black-500">
                            apg.gg/{event.owner.username}
                          </Link>
                        </div>
                      </div>
                      
                      {event.owner.id !== userId && event.userParticipant.data.total === 0 ? (
                        <Button
                          onClick={handleJoinEvent}
                          fontSize="sm"
                          disabled={joinEventMutation.isPending}
                        >
                          {joinEventMutation.isPending ? <PulseRingIcon /> : null}
                          {t("label-join-event")}
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <h2 className="text-title-lg text-white ">{t("subtitle-participants")}</h2>

              <AnimatePresence>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <InfiniteScroll
                    data={data}
                    fetchFunction={fetchNextPage}
                    status={status}
                    isFetchingNextPage={isFetchingNextPage}
                    hasNextPage={hasNextPage}
                    error={error as Error}
                    emptyMessage={t('this-event-has-no-participants')}
                    renderItem={(item: any) => (
                      <ErrorBoundary
                        FallbackComponent={({ error }) => (
                          <ErrorCardProfileFallback error={error} section="event-card-profile" />
                        )}
                        onError={logError}
                      >
                        <CardProfileFollow user={item } />
                      </ErrorBoundary>
                    )}
                  />
                </div>
              </AnimatePresence>
            </div>
          </div>
        ) : null}
      </section>
    </>
  )
}

export default EventDetailComponent;